import React, { useMemo } from 'react'
import styled from '@emotion/styled'
import { Locale, Translation } from 'locale'
import { CollapsePanel } from 'view/components/panels'
import { COUNTERPARTY_COLOURS } from './rfqPanelStyles'
import { DialogField, DialogRow } from 'view/components/inputs'
import { SettlementVenue, ValidationIssue } from 'api'
import { useAppDispatch } from 'utils'
import { AliasesMultiCombobox } from 'view/shared/entity/AliasesMultiCombobox'
import {
  corporateCollateralMultipleCounterpartyErrorMessage,
  gfidMissingErrorMessage,
  hasCorporateCollateralValidationsErrors,
  hasGfidValidationErrors
} from './rfqPanelUtils'
import { useSelector } from 'react-redux'
import { CounterpartyFilter, tradingEntitiesGetterSelector } from 'selectors/tradingEntitySelectors'
import { RfqPanelThunks } from './rfqPanelThunks'
import { preparedNegotiationInstructionSelector, preparedStagingOrdersSelector } from 'selectors/selectionSelectors'

interface CounterpartySectionProps {
  readOnly: boolean
}

const ContentWrapper = styled.div`
  width: 100%;
  padding: 15px 12px;

  & > ${DialogRow} {
    &:not(:last-child) {
      margin-bottom: 8px;
    }
  }
`

export const CounterpartySection: React.FC<CounterpartySectionProps> = ({ readOnly }) => {
  const dispatch = useAppDispatch()
  const ordersToPrepare = useSelector(preparedStagingOrdersSelector)
  const { counterpartyInstructions, settlementVenue, clearingHouseId } = useSelector(preparedNegotiationInstructionSelector)
  const selectedCounterpartyIds = counterpartyInstructions.map(ci => ci.counterpartyId)
  const getTradingEntities = useSelector(tradingEntitiesGetterSelector)
  const counterpartyTradingEntities = getTradingEntities(selectedCounterpartyIds)

  const counterpartyFilters = useMemo(
    () =>
      ordersToPrepare.map<CounterpartyFilter>(orderToPrepare => ({
        isin: orderToPrepare.orderDetail.order.collateralId,
        bui: orderToPrepare.orderDetail.order.basketAllocation?.basketId ?? null,
        secondaryCollateralId: orderToPrepare.orderDetail.secondaryCollateral?.id ?? null,
        secondaryBasketId: orderToPrepare.orderDetail.secondaryCollateralBasket?.id ?? null,
        fundId: orderToPrepare.orderDetail.order.fundId,
        repoType: orderToPrepare.orderDetail.order.repoType,
        masterLegalAgreementType: orderToPrepare.orderDetail.order.masterLegalAgreementType,
        marketType: orderToPrepare.orderDetail.order.marketType
      })),
    [ordersToPrepare]
  )

  const subtitle = useMemo(
    () =>
      Locale.translate(Translation.SC_COUNTERPARTIES_SUMMARY, {
        size: selectedCounterpartyIds.length
      }),
    [selectedCounterpartyIds]
  )

  const areCounterpartiesReadOnly = useMemo(() => {
    if (readOnly) {
      return true
    }

    if (settlementVenue === SettlementVenue.Bilateral || settlementVenue === SettlementVenue.Triparty) {
      return false
    }

    // disable counterparty selection if this is a cleared trade and the user hasn't selected a clearing house yet
    return clearingHouseId === null
  }, [readOnly, settlementVenue, clearingHouseId])

  const validationIssues = useMemo((): ValidationIssue[] | undefined => {
    if (hasCorporateCollateralValidationsErrors(ordersToPrepare, counterpartyInstructions)) {
      return [corporateCollateralMultipleCounterpartyErrorMessage]
    } else if (hasGfidValidationErrors(ordersToPrepare, counterpartyTradingEntities)) {
      return [gfidMissingErrorMessage]
    } else {
      return undefined
    }
  }, [counterpartyInstructions, counterpartyTradingEntities, ordersToPrepare])

  return (
    <CollapsePanel
      dataTestId={'SendRFQPanel-Counterparties'}
      title={Locale.translate(Translation.TC_COUNTERPARTIES)}
      subtitle={subtitle}
      defaultCollapsed={false}>
      <ContentWrapper>
        <DialogRow>
          <DialogField>
            <AliasesMultiCombobox
              filters={counterpartyFilters}
              dataTestId={'CounterpartySection-CounterpartyGroupedMultiCombobox'}
              clearingHouseId={clearingHouseId}
              settlementVenue={settlementVenue}
              placeholder={Locale.translate(Translation.SC_SELECT_COUNTERPARTY)}
              optionColorPalette={COUNTERPARTY_COLOURS}
              value={counterpartyInstructions}
              readOnly={false}
              disabled={areCounterpartiesReadOnly}
              canClear={!areCounterpartiesReadOnly}
              clearButtonLabel={Locale.translate(Translation.SC_REMOVE_COUNTERPARTIES)}
              onUpdate={counterpartyInstructions => dispatch(RfqPanelThunks.changeCounterpartyIds(counterpartyInstructions))}
              validationIssues={validationIssues}
            />
          </DialogField>
        </DialogRow>
      </ContentWrapper>
    </CollapsePanel>
  )
}
